import apiService from './api';
import { 
  SessionConfig, 
  SessionInfo, 
  SessionMessage,
  WorkflowStep 
} from '../types';

// Update step session configuration
export const updateStepSessionConfig = async (
  stepId: string,
  sessionConfig: SessionConfig
): Promise<WorkflowStep> => {
  return await apiService.updateWorkflowStep(stepId, {
    session_config: sessionConfig
  });
};

// Get session history/messages
export const getSessionHistory = async (
  sessionKey: string
): Promise<SessionMessage[]> => {
  const response = await apiService.get(`/sessions/${sessionKey}/messages`);
  return response.data;
};

// Clear session history
export const clearSessionHistory = async (
  sessionKey: string
): Promise<void> => {
  // Use the internal api instance since apiService doesn't expose delete method
  await (apiService as any).api.delete(`/sessions/${sessionKey}/messages`);
};

// Get session info
export const getSessionInfo = async (
  sessionKey: string
): Promise<SessionInfo> => {
  const response = await apiService.get(`/sessions/${sessionKey}/info`);
  return response.data;
};

// Clear all sessions for a workflow
export const clearAllWorkflowSessions = async (
  workflowId: string
): Promise<void> => {
  await (apiService as any).api.delete(`/sessions/orchestrations/${workflowId}/sessions`);
};

// Get all sessions for a workflow
export const getWorkflowSessions = async (
  workflowId: string
): Promise<SessionInfo[]> => {
  const response = await apiService.get(`/sessions/orchestrations/${workflowId}/sessions`);
  return response.data;
};

// Get session statistics for a workflow
export const getSessionStatistics = async (
  workflowId: string
): Promise<{
  total_sessions: number;
  active_sessions: number;
  total_messages: number;
  average_messages_per_session: number;
  memory_usage_bytes: number;
}> => {
  const response = await apiService.get(`/sessions/orchestrations/${workflowId}/sessions/statistics`);
  return response.data;
};

// Export session data
export const exportSessionData = async (
  sessionKey: string,
  format: 'json' | 'text' = 'json'
): Promise<Blob> => {
  const response = await apiService.get(`/sessions/${sessionKey}/export`, {
    params: { format },
    responseType: 'blob'
  });
  return response.data;
};

// Get session utilization metrics
export const getSessionUtilizationMetrics = async (
  projectId?: string,
  startDate?: string,
  endDate?: string
): Promise<{
  total_sessions: number;
  active_sessions: number;
  total_messages: number;
  sessions_by_mode: Record<string, number>;
  sessions_by_type: Record<string, number>;
  daily_activity: Array<{
    date: string;
    session_count: number;
    message_count: number;
  }>;
}> => {
  const response = await apiService.get('/sessions/utilization/metrics', {
    params: {
      project_id: projectId,
      start_date: startDate,
      end_date: endDate
    }
  });
  return response.data;
};